import IconChevronDown from '@apollo/icons/small/IconChevronDown.svg';
import {
  Button,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Spinner,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';

import { ConfirmationModal } from './confirmationModal/ConfirmationModal';

export type ConfirmationAction =
  | 'markAsSafe'
  | 'unmarkAsSafe'
  | 'ignore'
  | 'unignore';

interface Props {
  graphId: string;
  exactlyOneChange: boolean;
  loading: boolean;
  markAsSafe: () => void;
  ignoreOperations: () => void;
}
export const OverrideButton = ({
  graphId,
  exactlyOneChange,
  loading,
  markAsSafe,
  ignoreOperations,
}: Props) => {
  const [confirmAction, setConfirmAction] = useState<ConfirmationAction | null>(
    null,
  );

  return (
    <>
      <PermissionWarning
        graphId={graphId}
        graphPermissions="canWriteCheckConfiguration"
        childRenderer={(hasPermission) => (
          <Menu placement="bottom-start">
            <MenuButton
              as={Button}
              rightIcon={loading ? <Spinner size="xs" /> : <IconChevronDown />}
              className="ml-4"
              variant="secondary"
              isDisabled={!hasPermission || loading}
            >
              Override
            </MenuButton>
            <MenuContent>
              <MenuList>
                <MenuItem
                  description={`Do not fail future checks when ${
                    exactlyOneChange
                      ? ' this change is '
                      : ' these changes are '
                  } observed for this operation.`}
                  onClick={() => setConfirmAction('markAsSafe')}
                >
                  Mark change{exactlyOneChange ? '' : 's'} as safe
                </MenuItem>
                <MenuItem
                  description="Do not fail future checks when any breaking changes to this operation are observed."
                  onClick={() => setConfirmAction('ignore')}
                >
                  Ignore operation
                </MenuItem>
              </MenuList>
            </MenuContent>
          </Menu>
        )}
      />
      <ConfirmationModal
        isOpen={confirmAction === 'markAsSafe'}
        title={`Mark change${exactlyOneChange ? '' : 's'} as safe?`}
        description={
          <>
            By selecting this action, you acknowledge that the change
            {exactlyOneChange ? '' : 's'} for this operation will not affect the
            clients that execute it.
          </>
        }
        onSubmit={() => markAsSafe()}
        onClose={() => setConfirmAction(null)}
      />
      <ConfirmationModal
        isOpen={confirmAction === 'ignore'}
        title="Ignore this operation?"
        description={
          <>
            By selecting this action, you acknowledge that this specific
            operation is no longer in use. Changes affecting it will continue to
            be tracked, but won't affect the outcome of future checks.
          </>
        }
        onSubmit={() => ignoreOperations()}
        onClose={() => setConfirmAction(null)}
      />
    </>
  );
};
